import React, { useState, useContext } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CForm,
  CInput,
  CInputGroup,
  CInputGroupPrepend,
  CInputGroupText,
  CRow,
  CFormGroup,
  CLabel,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import userService from "../../services/Users/userServiceClient";
import { UserContext } from "../../UserContext";
import authServiceClient from "../../services/Application/authServiceClient";

const ForgotPassword = () => {
  const { user, logout } = useContext(UserContext);
  const [step, setStep] = useState(1); // 1: Email, 2: Verification Code, 3: Reset Password
  const [email, setEmail] = useState(user?.email || "");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "Email is required";
    if (!emailRegex.test(email)) return "Invalid email format";
    return "";
  };

  const validateOtp = (otp) => {
    if (!otp) return "Verification code is required";
    if (!/^\d{6}$/.test(otp)) return "Verification code must be 6 digits";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password))
      return "Password must contain at least one lowercase letter";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter";
    if (!/[0-9]/.test(password))
      return "Password must contain at least one number";
    return "";
  };

  const validateConfirmPassword = (confirm, password) => {
    if (!confirm) return "Please confirm your password";
    if (confirm !== password) return "Passwords do not match";
    return "";
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const emailError = validateEmail(email);

    if (emailError) {
      setErrors({ email: emailError });
      setTouched({ email: true });
      return;
    }

    setLoading(true);
    try {
      const result = await userService.forgotPassword(email);
      if (result.succeeded) {
        toast.success(
          result.additionalMessage || "Verification code sent to your email",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          },
        );
        setStep(2);
        setErrors({});
      }
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpError = validateOtp(otpCode);

    if (otpError) {
      setErrors({ otp: otpError });
      setTouched({ otp: true });
      return;
    }

    setLoading(true);
    try {
      const result = await userService.validateOtp(email, otpCode);
      if (result.succeeded) {
        toast.success(
          result.additionalMessage ||
            "Verification code validated successfully",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          },
        );
        setStep(3);
        setErrors({});
      }
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    const passwordError = validatePassword(newPassword);
    const confirmError = validateConfirmPassword(confirmPassword, newPassword);

    if (passwordError || confirmError) {
      setErrors({
        newPassword: passwordError,
        confirmPassword: confirmError,
      });
      setTouched({ newPassword: true, confirmPassword: true });
      return;
    }

    setLoading(true);
    try {
      const result = await userService.resetPassword(
        email,
        otpCode,
        newPassword,
        confirmPassword,
      );
      if (result.succeeded) {
        toast.success(
          result.additionalMessage || "Password reset successfully",
          {
            position: toast.POSITION.BOTTOM_RIGHT,
          },
        );
        setTimeout(async () => {
          if (user) {
            logout();
            await authServiceClient.logout();
          } else {
            window.location.href = "#/login";
          }
        }, 2000);
      }
    } catch (error) {
      //
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
      setTouched({});
    }
  };

  return (
    <div className="login-container">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md="6" lg="4">
            <CCard className="login-card p-4">
              <CCardHeader className="login-header text-center">
                <h3>
                  {step === 1 && (user ? "Change Password" : "Forgot Password")}
                  {step === 2 && "Verify Code"}
                  {step === 3 && "Reset Password"}
                </h3>
                <img
                  src="/logo_eif.png"
                  alt="EIF Logo"
                  style={{ height: "60px", marginTop: "10px" }}
                />
              </CCardHeader>
              <CCardBody>
                {/* Step 1: Email Input */}
                {step === 1 && (
                  <CForm onSubmit={handleEmailSubmit}>
                    <p className="text-muted text-center mb-4">
                      Enter your email to receive a verification code
                    </p>
                    <CFormGroup>
                      <CLabel htmlFor="email">Email address</CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-envelope-closed" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          type="email"
                          placeholder="Enter your email"
                          autoComplete="email"
                          value={email}
                          disabled={!!user}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (touched.email) {
                              setErrors({
                                ...errors,
                                email: validateEmail(e.target.value),
                              });
                            }
                          }}
                          onBlur={() => handleBlur("email")}
                          invalid={touched.email && !!errors.email}
                        />
                      </CInputGroup>
                      {touched.email && errors.email && (
                        <div className="text-danger small mt-1">
                          {errors.email}
                        </div>
                      )}
                    </CFormGroup>
                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      block
                      className="mb-3"
                    >
                      {loading ? (
                        <CSpinner size="sm" />
                      ) : (
                        "Send Verification Code"
                      )}
                    </CButton>
                    <div className="text-center">
                      <CButton
                        color="link"
                        onClick={async () => {
                          if (user) {
                            logout();
                            await authServiceClient.logout();
                          } else {
                            window.location.href = "#/login";
                          }
                        }}
                      >
                        Back to Login
                      </CButton>
                    </div>
                  </CForm>
                )}

                {/* Step 2: Verification Code Input */}
                {step === 2 && (
                  <CForm onSubmit={handleOtpSubmit}>
                    <p className="text-muted text-center mb-4">
                      Enter the 6-digit verification code sent to{" "}
                      <strong>{email}</strong>
                    </p>
                    <CFormGroup>
                      <CLabel htmlFor="otp">Verification Code</CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-shield-alt" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          type="text"
                          placeholder="Enter 6-digit code"
                          maxLength="6"
                          value={otpCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setOtpCode(value);
                            if (touched.otp) {
                              setErrors({ ...errors, otp: validateOtp(value) });
                            }
                          }}
                          onBlur={() => handleBlur("otp")}
                          invalid={touched.otp && !!errors.otp}
                        />
                      </CInputGroup>
                      {touched.otp && errors.otp && (
                        <div className="text-danger small mt-1">
                          {errors.otp}
                        </div>
                      )}
                    </CFormGroup>
                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      block
                      className="mb-3"
                    >
                      {loading ? <CSpinner size="sm" /> : "Validate Code"}
                    </CButton>
                    <div className="text-center">
                      <CButton
                        color="link"
                        onClick={handleBack}
                        disabled={loading}
                      >
                        Back
                      </CButton>
                    </div>
                  </CForm>
                )}

                {/* Step 3: Password Reset */}
                {step === 3 && (
                  <CForm onSubmit={handlePasswordReset}>
                    <p className="text-muted text-center mb-4">
                      Enter your new password
                    </p>
                    <CFormGroup>
                      <CLabel htmlFor="newPassword">New Password</CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-lock-locked" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          type="password"
                          placeholder="New Password"
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (touched.newPassword) {
                              setErrors({
                                ...errors,
                                newPassword: validatePassword(e.target.value),
                                confirmPassword: confirmPassword
                                  ? validateConfirmPassword(
                                      confirmPassword,
                                      e.target.value,
                                    )
                                  : "",
                              });
                            }
                          }}
                          onBlur={() => handleBlur("newPassword")}
                          invalid={touched.newPassword && !!errors.newPassword}
                        />
                      </CInputGroup>
                      {touched.newPassword && errors.newPassword && (
                        <div className="text-danger small mt-1">
                          {errors.newPassword}
                        </div>
                      )}
                    </CFormGroup>
                    <CFormGroup>
                      <CLabel htmlFor="confirmPassword">
                        Confirm Password
                      </CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-lock-locked" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          type="password"
                          placeholder="Confirm Password"
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (touched.confirmPassword) {
                              setErrors({
                                ...errors,
                                confirmPassword: validateConfirmPassword(
                                  e.target.value,
                                  newPassword,
                                ),
                              });
                            }
                          }}
                          onBlur={() => handleBlur("confirmPassword")}
                          invalid={
                            touched.confirmPassword && !!errors.confirmPassword
                          }
                        />
                      </CInputGroup>
                      {touched.confirmPassword && errors.confirmPassword && (
                        <div className="text-danger small mt-1">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </CFormGroup>
                    <small className="text-muted d-block mb-3">
                      Password must be at least 8 characters with uppercase,
                      lowercase, and number
                    </small>
                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      block
                      className="mb-3"
                    >
                      {loading ? <CSpinner size="sm" /> : "Reset Password"}
                    </CButton>
                    <div className="text-center">
                      <CButton
                        color="link"
                        onClick={handleBack}
                        disabled={loading}
                      >
                        Back
                      </CButton>
                    </div>
                  </CForm>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default ForgotPassword;
